/**
 * TestCaseRow — a single row in the virtualized test case list.
 *
 * Shows: checkbox, tcId badge, title, severity chip, caseType chip,
 * type badge, tags (on hover when tags exist), action buttons.
 *
 * Subscribes ONLY to `selectedIds` and `selectedTestCaseId` slices
 * so it does not re-render on folder changes.
 *
 * isReorderMode: when true, disables click/checkbox and renders the row
 * in a drag-friendly style. The actual sortable drag mechanics are handled
 * by the SortableTestCaseRow wrapper in TestCaseList — this component only
 * adjusts its visual appearance (no useSortable hook here to avoid conflicts).
 */

import { memo, useCallback } from 'react';
import { FlaskConical, Lock, GripVertical } from 'lucide-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import { SeverityBadge } from '../shared/SeverityBadge';
import type { ITestCaseRow } from '../types';
import { CASE_TYPE_LABELS } from '../types';

interface TestCaseRowProps {
    testCase:      ITestCaseRow;
    style?:        React.CSSProperties;   // passed by the virtualizer in normal mode
    /** Whether this row is currently the active drag-over target for reordering. */
    isDropTarget?: boolean;
    /** 'before' renders a line at the top edge; 'after' at the bottom edge. */
    dropPosition?: 'before' | 'after' | null;
    /** When true, disables click/checkbox; row is wrapped in SortableTestCaseRow. */
    isReorderMode?: boolean;
}

export const TestCaseRow = memo(function TestCaseRow({
    testCase,
    style,
    isDropTarget = false,
    dropPosition = null,
    isReorderMode = false,
}: TestCaseRowProps) {
    const toggleSelected     = useTestCasesStore((s) => s.toggleSelected);
    const selectedIds        = useTestCasesStore((s) => s.selectedIds);
    const selectedTestCaseId = useTestCasesStore((s) => s.selectedTestCaseId);
    const setSelected        = useTestCasesStore((s) => s.setSelectedTestCaseId);

    const isChecked  = selectedIds.has(testCase._id);
    const isActive   = selectedTestCaseId === testCase._id;

    // Draggable + droppable — used in normal mode only (disabled in reorder mode,
    // where the SortableTestCaseRow wrapper handles drag).
    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
        id:   testCase._id,
        data: { type: 'testCase', label: testCase.tcId ?? testCase.title },
        disabled: isReorderMode,
    });
    const { setNodeRef: setDropRef } = useDroppable({
        id:   testCase._id,
        data: { type: 'testCase' },
        disabled: isReorderMode,
    });

    // In normal mode, merge both refs; in reorder mode the sortable wrapper owns the ref.
    const mergedRef = useCallback(
        (el: HTMLElement | null) => {
            if (!isReorderMode) { setDragRef(el); setDropRef(el); }
        },
        [isReorderMode, setDragRef, setDropRef],
    );

    function handleCheckbox(e: React.ChangeEvent<HTMLInputElement>) {
        e.stopPropagation();
        toggleSelected(testCase._id);
    }

    function handleRowClick() {
        if (isReorderMode) return;
        setSelected(testCase._id);
    }

    const caseTypeLabel = testCase.caseType ? (CASE_TYPE_LABELS[testCase.caseType] ?? testCase.caseType) : null;

    return (
        <div
            ref={mergedRef}
            role="option"
            aria-selected={isChecked}
            aria-label={`${testCase.tcId ?? ''} ${testCase.title}`}
            style={{ ...style, opacity: isDragging ? 0.4 : 1 }}
            onClick={handleRowClick}
            className={`group relative flex items-center gap-2 px-3 min-h-14 py-2 border-b border-slate-100 dark:border-slate-800 select-none transition-colors
                ${isReorderMode
                    ? 'cursor-grab active:cursor-grabbing hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    : isActive
                        ? 'cursor-pointer bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500'
                        : isChecked
                            ? 'cursor-pointer bg-blue-50/50 dark:bg-blue-900/10'
                            : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60'
                }`}
        >
            {/* Reorder insertion lines — normal mode only */}
            {!isReorderMode && isDropTarget && dropPosition === 'before' && (
                <div className="absolute inset-x-0 -top-px h-0.5 bg-blue-500 z-20 pointer-events-none rounded-full" />
            )}
            {!isReorderMode && isDropTarget && dropPosition === 'after' && (
                <div className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-500 z-20 pointer-events-none rounded-full" />
            )}

            {/* Grip icon — desktop only; interactive in normal mode, decorative in reorder mode */}
            <span
                {...(!isReorderMode ? attributes : {})}
                {...(!isReorderMode ? listeners : {})}
                onClick={!isReorderMode ? (e) => e.stopPropagation() : undefined}
                className={`hidden md:flex flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300
                    ${isReorderMode
                        ? ''
                        : 'opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing'
                    }`}
                title={isReorderMode ? 'Drag to reorder' : 'Drag to move to folder'}
            >
                <GripVertical size={13} />
            </span>

            {/* Checkbox — hidden in reorder mode */}
            {!isReorderMode && (
                <input
                    type="checkbox"
                    aria-label={`Select ${testCase.title}`}
                    checked={isChecked}
                    onChange={handleCheckbox}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer"
                />
            )}

            {/* Main content */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
                {/* Line 1: Title + tcId badge */}
                <div className="flex items-center gap-1.5 min-w-0">
                    {/* Title */}
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                        {testCase.title}
                    </span>

                    {/* tcId badge */}
                    {testCase.tcId && (
                        <span className="inline-flex items-center px-1.5 py-1 rounded text-[10px] font-mono font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 flex-shrink-0 leading-none">
                            {testCase.tcId}
                        </span>
                    )}

                    {/* Quarantine indicator */}
                    {testCase.isQuarantined && (
                        <Lock size={11} className="text-amber-500 flex-shrink-0" title="Quarantined" />
                    )}
                </div>

                {/* Line 2: Chips row — always wraps cleanly */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    <SeverityBadge severity={testCase.severity} />

                    {caseTypeLabel && (
                        <span className="inline-flex items-center px-1.5 py-1 rounded text-[10px] font-medium leading-none bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300">
                            {caseTypeLabel}
                        </span>
                    )}

                    {/* Type badge */}
                    {testCase.type === 'AUTOMATED' && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-1 rounded text-[10px] font-medium leading-none bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">
                            <FlaskConical size={9} />
                            Auto
                        </span>
                    )}

                    {/* Tags — visible on hover or when filters active */}
                    {testCase.tags && testCase.tags.length > 0 && (
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 flex-wrap">
                            {testCase.tags.slice(0, 3).map((tag) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center px-1.5 py-1 rounded-full text-[10px] leading-none bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                                >
                                    {tag}
                                </span>
                            ))}
                            {testCase.tags.length > 3 && (
                                <span className="text-[10px] text-slate-400">+{testCase.tags.length - 3}</span>
                            )}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
});
